import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  ChevronUp,
  Minus,
} from "lucide-react";
import { useDeliveryHub } from "@/contexts/DeliveryHubContext";
import {
  ChatMessage,
  getChatMessages,
  sendChatMessage,
  markChatRead,
} from "@/actions/delivery-hub";
import { socketService } from "@/services/socketService";
import { playNotificationSound } from "@/services/chatNotification";

const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const formatTime = (dateStr: string) => {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  }
  return `${d.getDate()} ${monthShort[d.getMonth()]} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

// Layout constants
const WINDOW_WIDTH = 288;
const WINDOW_GAP = 8;
const BUTTON_RIGHT = 24;
const BUTTON_WIDTH = 52;
// First window starts just left of the floating button
const FIRST_WINDOW_RIGHT = BUTTON_RIGHT + BUTTON_WIDTH + WINDOW_GAP;

interface OpenChat {
  slateId: number;
  name: string;
  minimized: boolean;
}

// ─── Chat Window Panel ────────────────────────────────────────────────────────
interface ChatWindowPanelProps {
  slateId: number;
  schoolName: string;
  minimized: boolean;
  index: number;
  unread: number;
  onClose: () => void;
  onToggleMinimize: () => void;
  onRead: () => void;
  onActive: (id: number | null) => void;
}

const ChatWindowPanel = ({
  slateId,
  schoolName,
  minimized,
  index,
  unread,
  onClose,
  onToggleMinimize,
  onRead,
  onActive,
}: ChatWindowPanelProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastMsgTimeRef = useRef<string | null>(null);
  const minimizedRef = useRef(minimized);
  useEffect(() => { minimizedRef.current = minimized; }, [minimized]);

  // The admin is looking at this thread: record it as read on the server, then resync badges
  const markSeen = useCallback(async () => {
    await markChatRead(slateId);
    onRead();
  }, [slateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const rightOffset = FIRST_WINDOW_RIGHT + index * (WINDOW_WIDTH + WINDOW_GAP);

  const load = useCallback(async (p: number, prepend = false) => {
    if (p === 1) setLoading(true); else setLoadingMore(true);
    const result = await getChatMessages(slateId, p);
    setTotalPages(result.pages);
    if (prepend) {
      setMessages((prev) => [...result.messages, ...prev]);
    } else {
      setMessages(result.messages);
    }
    if (p === 1) setLoading(false); else setLoadingMore(false);
  }, [slateId]);

  // Keep lastMsgTimeRef in sync
  useEffect(() => {
    if (messages.length > 0) {
      lastMsgTimeRef.current = messages[messages.length - 1].created_at;
    }
  }, [messages]);

  // Socket + polling setup — runs once per slateId
  useEffect(() => {
    setMessages([]);
    setPage(1);
    lastMsgTimeRef.current = null;
    load(1).then(() => onRead());

    const joinRoom = () => socketService.emit("join_chat_room", { slateId });
    joinRoom();
    socketService.on("connect", joinRoom);

    const mergeNew = (newMsgs: ChatMessage[]) => {
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.id));
        const fresh = newMsgs.filter((m) => !ids.has(m.id));
        return fresh.length > 0 ? [...prev, ...fresh] : prev;
      });
      // A client message landed in an open (not minimized) window — it's been seen
      if (!minimizedRef.current && newMsgs.some((m) => m.sender_type === "client")) markSeen();
    };

    // Filter by slateId — multiple windows each register this handler
    const handleNew = ({ slateId: sid, message }: { slateId: number; message: ChatMessage }) => {
      if (sid !== slateId) return;
      mergeNew([message]);
      if (message.sender_type === "client") playNotificationSound();
    };
    socketService.on("new_chat_message", handleNew);

    const pollTimer = setInterval(async () => {
      if (!lastMsgTimeRef.current) return;
      const result = await getChatMessages(slateId, 1, lastMsgTimeRef.current);
      if (result.messages.length > 0) mergeNew(result.messages);
    }, 15_000);

    return () => {
      socketService.off("connect", joinRoom);
      socketService.emit("leave_chat_room", { slateId });
      socketService.off("new_chat_message", handleNew);
      clearInterval(pollTimer);
    };
  }, [slateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track active chat & read state based on minimized toggle
  useEffect(() => {
    if (!minimized) {
      onActive(slateId);
      markSeen();
    } else {
      onActive(null);
    }
    return () => onActive(null);
  }, [minimized, slateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!minimized && !loading) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, minimized]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    setText("");
    inputRef.current?.focus();
    const result = await sendChatMessage(slateId, msg);
    if (result.success && result.message) {
      // The server broadcasts on save, so the room event may already have added it
      const sent = result.message;
      setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
    }
    setSending(false);
  };

  return (
    <div
      className="fixed z-50 bg-white rounded-t-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
      style={{
        bottom: 0,
        right: rightOffset,
        width: WINDOW_WIDTH,
        height: minimized ? 44 : 420,
        transition: "height 0.15s ease",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 bg-brand text-white cursor-pointer flex-shrink-0"
        style={{ height: 44 }}
        onClick={onToggleMinimize}
        title={minimized ? "Expand" : "Minimize"}
      >
        <div className="w-6 h-6 rounded-full bg-white/20 text-xs font-semibold flex items-center justify-center flex-shrink-0">
          {schoolName.charAt(0).toUpperCase()}
        </div>
        <span className="text-xs font-semibold flex-1 truncate">{schoolName}</span>
        {unread > 0 && minimized && (
          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full flex-shrink-0">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleMinimize(); }}
          className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
          title={minimized ? "Expand" : "Minimize"}
        >
          {minimized ? <ChevronUp size={12} /> : <Minus size={12} />}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
          title="Close"
        >
          <X size={12} />
        </button>
      </div>

      {/* Body — hidden when minimized */}
      {!minimized && (
        <>
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1 bg-gray-50 min-h-0">
            {page < totalPages && (
              <div className="flex justify-center py-1">
                <button
                  onClick={async () => { const p = page + 1; setPage(p); await load(p, true); }}
                  disabled={loadingMore}
                  className="flex items-center gap-1 text-[11px] text-brand disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 size={11} className="animate-spin" /> : <ChevronUp size={11} />}
                  Load earlier
                </button>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 size={18} className="animate-spin text-brand" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-10 text-gray-400 gap-2">
                <MessageCircle size={24} className="opacity-40" />
                <p className="text-xs">No messages yet</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isAdmin = msg.sender_type === "admin";
                return (
                  <div key={msg.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"} mb-1`}>
                    <div className={`max-w-[80%] flex flex-col ${isAdmin ? "items-end" : "items-start"}`}>
                      <span className="text-[10px] text-gray-400 mb-0.5 px-1">{msg.sender_name}</span>
                      <div
                        className={`px-3 py-1.5 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap break-words ${
                          isAdmin
                            ? "bg-brand text-white rounded-tr-sm"
                            : "bg-white text-gray-800 border border-gray-200 rounded-tl-sm"
                        }`}
                      >
                        {msg.message}
                      </div>
                      <span className="text-[10px] text-gray-400 mt-0.5 px-1">
                        {formatTime(msg.created_at)}
                        {isAdmin && <span className="ml-1">{msg.is_read_client ? "✓✓" : "✓"}</span>}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-gray-100 bg-white px-3 py-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a message…"
                className="flex-1 px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
              />
              <button
                onClick={send}
                disabled={!text.trim() || sending}
                className="w-7 h-7 rounded-full bg-brand text-white flex items-center justify-center hover:bg-brand-hover disabled:opacity-40 transition-colors flex-shrink-0"
              >
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── School List panel ────────────────────────────────────────────────────────
interface SchoolListProps {
  openChats: OpenChat[];
  onSelect: (slateId: number, name: string) => void;
}

const SchoolList = ({ openChats, onSelect }: SchoolListProps) => {
  const { schools, chatUnreadCounts } = useDeliveryHub();

  const sorted = [...schools].sort((a, b) => {
    const unreadA = chatUnreadCounts[a.slate_id] || 0;
    const unreadB = chatUnreadCounts[b.slate_id] || 0;
    if (unreadB !== unreadA) return unreadB - unreadA;
    return (a.slate_name || "").localeCompare(b.slate_name || "");
  });

  const totalUnread = Object.values(chatUnreadCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <MessageCircle size={16} className="text-brand" />
          <span className="text-sm font-semibold text-gray-800">School Chats</span>
          {totalUnread > 0 && (
            <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-brand text-white rounded-full">
              {totalUnread}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {sorted.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">No schools found</p>
        ) : (
          sorted.map((school) => {
            const unread = chatUnreadCounts[school.slate_id] || 0;
            const isOpen = openChats.some((c) => c.slateId === school.slate_id);
            return (
              <button
                key={school.slate_id}
                onClick={() => onSelect(school.slate_id, school.slate_name?.replace(/_/g, " ") || "")}
                className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left ${isOpen ? "bg-brand/5" : ""}`}
              >
                <div className="w-8 h-8 rounded-full bg-brand/10 text-brand text-xs font-semibold flex items-center justify-center flex-shrink-0">
                  {(school.slate_name || "S").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">
                    {school.slate_name?.replace(/_/g, " ")}
                  </div>
                  {school.city_name && (
                    <div className="text-[11px] text-gray-400 truncate">
                      {[school.area_name, school.city_name].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>
                {unread > 0 && (
                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-brand text-white rounded-full">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Main ChatWidget ──────────────────────────────────────────────────────────
const ChatWidget = () => {
  const location = useLocation();
  const { chatUnreadCounts, refreshChatUnread, schools, setActiveChatSlateId } = useDeliveryHub();
  const [showList, setShowList] = useState(false);
  const [openChats, setOpenChats] = useState<OpenChat[]>([]);

  const totalUnread = Object.values(chatUnreadCounts).reduce((a, b) => a + b, 0);

  // When the school list is opened, pre-select the current school if on a school page
  useEffect(() => {
    if (!showList) return;
    const match = location.pathname.match(/\/delivery-hub\/schools\/(\d+)/);
    if (!match) return;
    const slateId = Number(match[1]);
    if (openChats.some((c) => c.slateId === slateId)) return; // already open
    const school = schools.find((s) => s.slate_id === slateId);
    if (school) {
      handleSelectSchool(slateId, school.slate_name?.replace(/_/g, " ") || "");
    }
  }, [showList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectSchool = (slateId: number, name: string) => {
    setOpenChats((prev) => {
      const existing = prev.find((c) => c.slateId === slateId);
      if (existing) {
        return prev.map((c) => c.slateId === slateId ? { ...c, minimized: false } : c);
      }
      return [...prev, { slateId, name, minimized: false }];
    });
    setShowList(false);
  };

  const handleClose = (slateId: number) => {
    setOpenChats((prev) => prev.filter((c) => c.slateId !== slateId));
  };

  const handleToggleMinimize = (slateId: number) => {
    setOpenChats((prev) =>
      prev.map((c) => c.slateId === slateId ? { ...c, minimized: !c.minimized } : c)
    );
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setShowList((o) => !o)}
        className="fixed bottom-6 right-6 z-50 rounded-full bg-brand text-white shadow-lg hover:bg-brand-hover transition-all hover:scale-105 active:scale-95 flex items-center justify-center"
        style={{ width: BUTTON_WIDTH, height: BUTTON_WIDTH }}
        title="School Chats"
      >
        {showList ? <X size={22} /> : <MessageCircle size={22} />}
        {!showList && totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full border-2 border-white">
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
      </button>

      {/* School list popup */}
      {showList && (
        <div
          className="fixed bottom-24 right-6 z-50 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
          style={{ height: 420, maxHeight: "70vh" }}
        >
          <SchoolList openChats={openChats} onSelect={handleSelectSchool} />
        </div>
      )}

      {/* Open chat windows — stacked right-to-left from the floating button */}
      {openChats.map((chat, index) => (
        <ChatWindowPanel
          key={chat.slateId}
          slateId={chat.slateId}
          schoolName={chat.name}
          minimized={chat.minimized}
          index={index}
          unread={chatUnreadCounts[chat.slateId] || 0}
          onClose={() => handleClose(chat.slateId)}
          onToggleMinimize={() => handleToggleMinimize(chat.slateId)}
          onRead={refreshChatUnread}
          onActive={setActiveChatSlateId}
        />
      ))}
    </>
  );
};

export default ChatWidget;
